/**
 * E07-F04-T12 — a company that was already checked in an EARLIER request.
 *
 * Owner decision 2026-09-24 (docs/research/examples-analysis.md §3 row 4): research
 * runs in rounds. A company that an earlier request already checked and that the new
 * request's discovery found again stays in the new list, marked "information
 * collected <date> — refresh?", with a link to that earlier request. Refresh = a new
 * collection for THIS company only, and only from a user click (the AI check is paid;
 * no automatic re-runs, decision-log 2026-07-20).
 *
 * The mark is stored on the row as the private evidence key {@link PRIOR_COLLECTION_KEY}
 * (never serialised as evidence; `__` = private) and projected onto the list DTOs as
 * `priorCollection`. `reused` says whether the earlier verdict was carried into this
 * request (the check criteria are the same, so no paid call was needed) or only shown
 * for reference (the criteria differ, the row stays "not checked").
 *
 * Imports only zod + the AI-run status helper, so the barrel star re-export stays
 * self-contained.
 */
import { z } from 'zod';
import { isActiveAiRunStatus } from './research-ai-chain.js';
/** The private evidence key the mark lives under on `research_companies.evidence`. */
export const PRIOR_COLLECTION_KEY = '__priorCollection';
export const priorCollectionSchema = z.object({
    /** The earlier request where the data was actually collected. */
    requestId: z.number().int(),
    requestTitle: z.string(),
    /** The row of that request (research_companies.id). */
    rowId: z.number().int(),
    /** When the data was collected there (ISO; its AI check finished). */
    collectedAt: z.string(),
    /** The grade / score the earlier request got. */
    grade: z.string().nullable(),
    score: z.number().nullable(),
    /**
     * true  — the earlier verdict was copied into this request with no AI call
     *         (the check criteria of both requests are the same);
     * false — the criteria differ, so the verdict is shown for reference only and
     *         this row is still "not checked".
     */
    reused: z.boolean(),
    /**
     * Wire-only: a Refresh (the per-company AI check) is running on THIS row right now.
     * Never stored — {@link projectPriorCollection} derives it from the row's run status.
     */
    refreshing: z.boolean().default(false),
});
/**
 * Read the mark from a row's raw evidence. Returns null for a row without one or with
 * a malformed one (a bad blob never breaks the list). Pure.
 */
export function readPriorCollection(evidence) {
    if (!evidence || typeof evidence !== 'object')
        return null;
    const raw = evidence[PRIOR_COLLECTION_KEY];
    const parsed = priorCollectionSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
}
/**
 * The mark as the lists show it, or null. A mark stops being shown once THIS row has
 * its own finished check that is newer than the earlier collection (the user clicked
 * Refresh and it succeeded): the data is then this request's own. A copied verdict
 * keeps `aiRunFinishedAt === collectedAt`, so it still shows the mark. A failed
 * refresh keeps the mark (the earlier data is still what the row shows). Pure.
 */
export function projectPriorCollection(row) {
    const pc = readPriorCollection(row.evidence);
    if (!pc)
        return null;
    const status = (row.aiRunStatus ?? 'idle');
    const collected = Date.parse(pc.collectedAt);
    const finishedRaw = row.aiRunFinishedAt;
    const finished = finishedRaw == null
        ? NaN
        : finishedRaw instanceof Date
            ? finishedRaw.getTime()
            : Date.parse(finishedRaw);
    if (status === 'scored' && Number.isFinite(finished) && Number.isFinite(collected)) {
        if (finished > collected)
            return null;
    }
    return { ...pc, refreshing: isActiveAiRunStatus(status) };
}
/** `YYYY-MM-DD` of an ISO timestamp ('' when unparsable). Pure. */
export function priorCollectionDate(iso) {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}
/** The line the lists show next to the company. Pure. */
export function priorCollectionLabel(pc) {
    return `Information collected ${priorCollectionDate(pc.collectedAt)} — refresh?`;
}
/** Tooltip on the Refresh button: what it does and that it is paid. */
export const PRIOR_COLLECTION_REFRESH_HINT = 'Collect the data for this company again (paid AI check, only this company).';
//# sourceMappingURL=research-prior-collection.js.map